import { QueryState } from "@/ds/custom/QueryState";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { useAbortImport } from "@/features/import/api/useAbortImport";
import { useConfirmImport } from "@/features/import/api/useConfirmImport";
import { useImportStatus } from "@/features/import/api/useImportStatus";
import { ImportEditor } from "@/features/import/components/ImportEditor";
import { ImportUpload } from "@/features/import/components/ImportUpload";
import { useNotifyMutationError } from "@/lib/common/useNotifyMutationError";
import { useNotifyMutationResult } from "@/lib/common/useNotifyMutationResult";
import { useTitle } from "@/lib/common/useTitle";
import { useEffect, useMemo, type FC } from "react";
import { useNavigate, useParams } from "react-router";

export const PageImport: FC = () => {
	useTitle("Import CSV");

	const collectionId = useParams()["collectionId"];
	const importStatus = useImportStatus(collectionId);
	const abortImport = useAbortImport();
	const importId = useMemo(() => importStatus.data?.import?.importId, [importStatus.data?.import?.importId]);
	const invalidRows = useMemo(() => importStatus.data?.import?.invalidRowCount !== 0, [importStatus.data?.import?.invalidRowCount]);
	const noValidRows = useMemo(() => importStatus.data?.import?.validRowCount === 0, [importStatus.data?.import?.validRowCount]);
	useNotifyMutationError(abortImport);

	return (
		<>
			{
				collectionId
					? <HeaderImport
						invalidRows={invalidRows}
						noValidRows={noValidRows}
						importId={importId}
						onAbort={abortImport.mutate}
						onAbortPending={abortImport.isPending} />
					: <HeaderImportWithoutCollection
						onAbortPending={abortImport.isPending}
						invalidRows={invalidRows}
						noValidRows={noValidRows}
						importId={importId}
						onAbort={abortImport.mutate} />
			}

			<QueryState query={importStatus}>
				{
					data => data.import
						? <ImportEditor import={data.import} />
						: <ImportUpload collectionId={collectionId} />
				}
			</QueryState>
		</>
	);
};

interface HeaderProps {
	importId: string | undefined;
	invalidRows: boolean;
	noValidRows: boolean;
	onAbortPending: boolean;
	onAbort: () => void;
}

const HeaderImportWithoutCollection = (props: HeaderProps) => {
	const {
		importId,
		invalidRows,
		noValidRows,
		onAbortPending,
		onAbort
	} = props;

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Collections", href: "/collections" },
		{ label: "Import CSV" }
	], []);

	return (
		<>
			<HeaderBar title="Import CSV" description="Create a new collection from a CSV file" breadcrumb={breadcrumb} />

			<HeaderImport
				invalidRows={invalidRows}
				noValidRows={noValidRows}
				importId={importId}
				onAbort={onAbort}
				onAbortPending={onAbortPending} />
		</>
	);
};

const HeaderImport = (props: HeaderProps) => {
	const {
		invalidRows,
		noValidRows,
		importId,
		onAbortPending,
		onAbort
	} = props;

	const confirmImport = useConfirmImport();
	const navigate = useNavigate();
	useNotifyMutationResult(confirmImport, "Import Confirmed");

	useEffect(() => {
		if (!confirmImport.data) {
			return;
		}

		navigate(`/collections/${confirmImport.data.body.collectionId}`);
	}, [confirmImport.data]);

	return (
		<>
			<HeaderBarTools>
				{
					importId &&
					<>
						<Button
							className="@md:w-34"
							variant="outline"
							disabled={invalidRows || noValidRows || confirmImport.isPending}
							onClick={() => confirmImport.mutate(importId)}>

							{
								confirmImport.isPending ? <Spinner /> : "Confirm Import"
							}
						</Button>

						<Button
							className="@md:w-34"
							variant="outline"
							disabled={onAbortPending}
							onClick={onAbort}>

							{
								onAbortPending ? <Spinner /> : "Abort"
							}
						</Button>
					</>
				}
			</HeaderBarTools>
		</>
	);
};
